import { defineStore } from 'pinia'

import ability, { buildAbilityRules } from '@/plugins/casl/ability'
import type { UserAbility } from '@/plugins/casl/AppAbility'
import axios from '@axios'
import { get } from '@core/utils'

// Singleton guard for get() — the router's beforeEach guard (router/index.ts) calls
// user.get() whenever `!user.data.uuid`, but that flag only flips once /profile
// resolves inside get() itself, so it's not a mutex: two navigations firing before
// the first resolves both see the gate open and each fire their own GET /profile +
// GET /me/providers pair. Those overlapping requests share one browser session
// cookie, and the server's SlideSessionExpiry middleware writes to that session on
// every authenticated request — so the pair can race the server-side session
// read/write and intermittently 401. Concurrent callers now share one in-flight
// promise instead of firing duplicate request pairs.
let getInFlight: Promise<UserModel> | null = null

/**
 * Forget a get() that will never settle, so the next one sends a fresh request.
 *
 * A 401 on /profile is answered by the axios interceptor with a promise that never
 * settles (it logs out instead). The shared in-flight promise would then hang every later
 * get() in this page, including the router guard's after the user logs back in with a 2FA
 * code, which leaves them on /verify. Logout calls this.
 */
export function abandonInFlightUserGet(): void {
  getInFlight = null
}

export function useUserStore() {
  return defineStore('user', {
    state: () => ({
      error: '' as string | null,
      isFetching: false,
      data: {} as UserModel,
      providers: [] as UserProviderEntry[],
    }),
    getters: {
      access: state => get(state, 'data.access', {}),
      hasRole: state => (roleList: string[]) =>
        get(state, 'data.roles', []).some((role: { name: string }) => roleList.includes(role.name)),
      hasPermission: state => (permissionList: string[]) =>
        get(state, 'data.access', {}).vector
        || get(state, 'data.permissions', []).some((permission: { name: string }) =>
          permissionList.includes(permission.name),
        ),
      isReady: state =>
        Boolean(get(state, 'data.permissions')) || Boolean(get(state, 'data.access')),

      // The provider the user is currently acting as, per the server-persisted
      // `User::currentProvider()` — mirrored client-side from GET /me/providers'
      // `is_active` flag rather than re-derived from `access.provider`.
      activeProvider: state => state.providers.find(p => p.is_active) ?? null,

      // Provider users may belong to more than one provider organization — this
      // gates the provider-switcher UI (SelectProvider.vue picker, UserProfile.vue
      // menu item) so single-provider users never see them.
      hasMultipleProviders: state => state.providers.length > 1,
    },
    actions: {
      async refreshData() {
        this.setFetching(true)
        try {
          const response = await axios.get<UserModel>('/profile', {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache',
              'Expires': '0',
            },
          })

          this.data = response.data
          ability.update(buildAbilityRules(response.data))
        } catch (error: any) {
          this.error = error.response?.data?.message || error.message
          this.setFetching(false)
        } finally {
          this.setFetching(false)
        }
      },
      get() {
        if (getInFlight)
          return getInFlight

        this.setFetching(true)

        getInFlight = new Promise<UserModel>((resolve, reject) => {
          axios
            .get<UserModel>('/profile')
            .then(async ({ data }) => {
              this.data = data
              ability.update(buildAbilityRules(data))

              // Best-effort — vector admins have no provider_user rows and this
              // legitimately returns an empty list for them, so a failure here
              // shouldn't block the rest of login.
              await this.fetchProviders().catch(() => {
                this.providers = []
              })

              this.setFetching(false)

              resolve(data)
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)

              reject(new Error(this.error || 'Request failed'))
            })
        }).finally(() => {
          getInFlight = null
        })

        return getInFlight
      },
      async fetchProviders() {
        const { data } = await axios.get<UserProviderEntry[]>('/me/providers')

        this.providers = data
      },

      // Called after a successful PATCH /me/active-provider — merges the
      // refreshed provider context the endpoint returns into the locally-held
      // provider list rather than re-fetching /me/providers from scratch.
      //
      // Keyed on uuid throughout, so a bigint id can't leak into a route param.
      // The response also carries a legacy `provider_id`, deliberately left out of
      // this type — nothing here should read it.
      async switchActiveProvider(providerUuid: string) {
        const { data } = await axios.patch<{ uuid: string; name: string; role: string }>(
          '/me/active-provider',
          { uuid: providerUuid },
        )

        this.providers = this.providers.map(p => ({
          ...p,
          ...(p.uuid === data.uuid ? { name: data.name, role: data.role } : {}),
          is_active: p.uuid === data.uuid,
        }))

        // Abilities are built server-side for the active hospital (role, entitlements, and since
        // VH-3574 whether this user works its Insurance Inbox), so reload them for the new one
        // before the caller routes there. Otherwise nav and route guards use the old hospital's.
        await this.refreshData()

        return data
      },
      update(data: Record<string, any>) {
        this.setFetching(true)

        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/user-information', data)
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      updatePassword(body: Record<string, any>) {
        this.setFetching(true)

        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/password', body)
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      verifyEmail(code: string) {
        this.setFetching(true)

        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/email-verify-change', { code })
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      updatePhone(phone: string) {
        this.setFetching(true)

        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/phone', { phone })
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      updateWorkPhone(work_phone: string) {
        this.setFetching(true)

        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/work-phone', { work_phone })
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      verifyPhone(code: string) {
        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/phone-verify-change', { code })
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      resendPhoneCode() {
        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/resend-verification-phone')
            .then(() => {
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      resendEmailCode() {
        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/resend-verification-email')
            .then(() => {
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              this.setFetching(false)
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      confirmPassword(password: string) {
        return new Promise<void>((resolve, reject) => {
          axios
            .post('/confirm-password', { password })
            .then(() => {
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      setupAuthenticator() {
        return new Promise<AuthenticatorSetup>((resolve, reject) => {
          axios
            .post<AuthenticatorSetup>('/profile/authenticator/setup')
            .then(({ data }) => {
              resolve(data)
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      confirmAuthenticator(code: string) {
        return new Promise<string[]>((resolve, reject) => {
          axios
            .post<{ success: boolean; recovery_codes: string[] }>('/profile/authenticator/confirm', { code })
            .then(({ data }) => {
              this.get()
              resolve(data.recovery_codes)
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      regenerateRecoveryCodes() {
        return new Promise<string[]>((resolve, reject) => {
          axios
            .post<{ recovery_codes: string[] }>('/profile/authenticator/recovery-codes/regenerate')
            .then(({ data }) => {
              resolve(data.recovery_codes)
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      disableAuthenticator() {
        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/authenticator/disable')
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      requestSmsTwoFactorCode() {
        return new Promise<string | undefined>((resolve, reject) => {
          axios
            .post<{ phone_last_4?: string }>('/profile/sms-2fa/request', { consent: true })
            .then(({ data }) => resolve(data?.phone_last_4))
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      enableSmsTwoFactor(code: string) {
        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/sms-2fa/enable', { consent: true, code })
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      disableSmsTwoFactor() {
        return new Promise<void>((resolve, reject) => {
          axios
            .post('/profile/sms-2fa/disable')
            .then(() => {
              this.get()
              resolve()
            })
            .catch((error: any) => {
              this.error = error.response?.data?.message || error.message
              reject(new Error(this.error || 'Request failed'))
            })
        })
      },
      setFetching(value: boolean) {
        this.isFetching = value
        if (value === true)
          this.error = null
      },
    },
  })()
}

export interface UserAccess {
  vector?: boolean
  provider?: Record<string, string> // key = provider UUID, value = role
}

// Shape of each entry returned by GET /me/providers.
export interface UserProviderEntry {

  // The provider's public identifier, and the ONLY value that resolves against
  // the `/api/providers/{provider}` routes (they bind on providers.uuid). Every
  // `provider_uuid` route param and provider-scoped URL must be built from this.
  //
  // The endpoint used to also return the internal bigint PK as `provider_id`,
  // and this client used it as the `provider_uuid` route param — which 404s as
  // "Provider not found." That field is gone from the payload; don't reintroduce
  // it, and note this type must not outrun the server (VectorHealthAI/server#2514).
  uuid: string

  name: string
  role: string
  is_active: boolean
}

export type TwoFactorType = 'email' | 'app' | 'phone'

export interface AuthenticatorSetup {
  secret: string
  otpauth_uri: string
  qr_code_svg: string
}

export interface UserModel {
  uuid: string
  created_at: string
  first_name: string
  last_name: string
  name: string
  email: string
  temp_email: string | null
  email_verified_at: string
  phone: string
  work_phone: string
  work_fax: string
  phone_verified_at: string | null
  two_factor_type: TwoFactorType
  temp_phone: string | null
  tfa_enabled: boolean
  two_factor_confirmed_at: string | null
  session_idle_timeout_minutes?: number
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  country: string | null
  zip: string | null
  avatar: string | null
  access: UserAccess
  abilities?: UserAbility[]

  /**
   * VH-2665. Keys of in-product education this user has already dismissed, at the
   * version currently shipping — the server omits a key whose stored version is behind
   * the current one, which is how rewritten content re-shows to people who dismissed
   * the old wording. Optional because it rides on /profile and any stale cached
   * payload predates it.
   */
  education_dismissals?: string[]
}
